import { useAppDispatch, useAppSelector } from "../../app/hooks";
import {
  provideAnswer,
  resetGame,
  selectAnswerState,
  selectCurrentQuestion,
  selectNextQuestion,
  selectOptions,
  selectPlay,
} from "../game-state/game-state.slice";
import React, { useCallback, useEffect, useState } from "react";
import { ProgressBar } from "./components/progress-bar";
import { EndScreen } from "./components/end-screen";
import { Question } from "../game-state/game-state.type";
import { SoundButton } from "./components/sound-button";
import { OptionGrid } from "./components/option-grid";
import {
  generateGame,
  incrementQuestion,
  loadGame,
  playSound,
} from "../game-state/games.functions";
import { useNavigate, useParams } from "react-router-dom";
import { BasicModal } from "../navigation/basic-modal";
import { InfoBox } from "../auth/info-box";

export const MatchGame = () => {
  const [soundPlaying, setSoundPlaying] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [modalOpen, setModalOpen] = useState(true);
  const options = useAppSelector(selectOptions);
  const currentQuestion = useAppSelector(selectCurrentQuestion);
  const nextQuestion = useAppSelector(selectNextQuestion);
  const play = useAppSelector(selectPlay);
  const answerState = useAppSelector(selectAnswerState);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  useEffect(() => {
    if (id) {
      dispatch(loadGame(id))
        .then(() => dispatch(generateGame(12, 4)))
        .catch(() => setLoadError(true));
    } else {
      navigate("/");
    }
    return () => {
      dispatch(resetGame());
    };
  }, [id, dispatch, navigate]);

  const handlePlaySound = useCallback(
    (question: Question) => {
      setSoundPlaying(true);
      setTimeout(
        () =>
          dispatch(
            playSound(`data/${options[question.correctOption].soundUrl}`, () =>
              setSoundPlaying(false)
            )
          ).catch(() => setSoundPlaying(false)),
        250
      );
    },
    [options, dispatch]
  );

  const handleClick = () => {
    if (play) {
      if (play.currentQuestionStatus === "answered") {
        if (nextQuestion) {
          handlePlaySound(nextQuestion);
        }
        dispatch(incrementQuestion());
      } else {
        if (currentQuestion) {
          handlePlaySound(currentQuestion);
        }
      }
    }
  };

  if (loadError) {
    return <LoadError id={id} />;
  }

  return (
    <>
      {modalOpen ? (
        <IntroModal
          dismiss={() => {
            handleClick();
            setModalOpen(false);
          }}
        />
      ) : null}

      <div className="container mx-auto">
        <div className={"food mt-8 sm:mt-16 sm:rounded shadow-xl"}>
          {answerState ? <ProgressBar answerState={answerState} /> : null}

          {play && !play.finished ? (
            <div>
              <SoundButton
                playing={soundPlaying}
                currentQuestionStatus={play.currentQuestionStatus}
                onClick={handleClick}
              />
              {currentQuestion ? (
                <OptionGrid
                  currentQuestion={currentQuestion}
                  currentQuestionStatus={play.currentQuestionStatus}
                  options={options}
                  provideAnswer={(option) => {
                    if (option === currentQuestion?.correctOption) {
                      dispatch(playSound("sfx/correct.mp3"));
                    } else {
                      dispatch(playSound("sfx/wrong.mp3"));
                    }
                    dispatch(provideAnswer(option));
                  }}
                />
              ) : null}
            </div>
          ) : answerState ? (
            <EndScreen answerState={answerState} />
          ) : null}
        </div>
      </div>
    </>
  );
};

const LoadError = ({ id }: { id: string | undefined }) => {
  return (
    <div
      className="flex items-center bg-red-500 text-white text-sm font-bold px-4 py-3 mb-6"
      role="alert"
    >
      <i className="fa fa-times mr-4" />
      <p>Er is iets misgegaan bij het laden van het spel met id: {id}</p>
    </div>
  );
};

const IntroModal = ({ dismiss }: { dismiss: () => void }) => {
  return (
    <BasicModal>
      <>
        <InfoBox>
          Klik op de blauwe luidspreker knop en luister goed naar de geluiden.
          Vind dan het juiste plaatje bij het geluid.
        </InfoBox>
        <button onClick={dismiss} className="btn-primary">
          <i className="fa fa-paw mr-2 fa-2x" />
          <span>Beginnen!</span>
        </button>
      </>
    </BasicModal>
  );
};
